<script setup lang="ts">
const { t } = useTexts();
const { getFormattedBrazilianDate } = useDateTimeHelper();
const { isAuthenticated } = storeToRefs(useAuthStore());
const { fetchUserProfile } = useUserData();
const { openToaster } = useLayoutStore();

const { isWithinMigrationPeriod, userMigrationDate, userMigrationNewDomain } =
  useFeatureFlagsNew();

const { redirectToPendingSettings } = useUserValidations();

const {
  userCanOnlyWithdraw,
  userIsOnExclusionOperatorActive,
  userExclusionOperatorUntil,
  userExclusionOperatorNote,
  userIsOnExclusionSpaActive,
  userExclusionSpaPeriod,
  userExclusionSpaUntil,
  userExclusionSpaNote,
  userIsOnExclusionActive,
  userIsOnExclusionPermanentActive,
  userExclusionUntil,
  userLimits,
  userIsOnTimeoutActive,
  userTimeoutUntil,
  isUserExceedTimeoutLimit,
} = useUserLimits();

const lastRequestTime = ref<number>(0);
const showLoading = ref<boolean>(false);

const { periodsOptions } = useAccountProtection();

function findPeriodLabel(period: string) {
  return periodsOptions.find((option) => {
    return option.value === period;
  })?.label;
}

const spaExclusionMessage = computed<string>(() => {
  if (!userExclusionSpaPeriod.value || !userExclusionSpaUntil.value) {
    return '';
  }

  if (userExclusionSpaPeriod.value >= 100) {
    if (userExclusionSpaPeriod.value === 102) {
      return t('account.user_data.exclusion_by_spa_prevented', {
        appName: appName(),
      });
    } else if (userExclusionSpaPeriod.value === 103) {
      return t('account.user_data.exclusion_by_spa_centralized');
    } else {
      return t('account.user_data.exclusion_by_spa_generic', {
        number: userExclusionSpaPeriod.value,
        appName: appName(),
      });
    }
  }

  if (userExclusionSpaPeriod.value === 99) {
    return t('account.user_data.exclusion_by_spa_permanent', {
      dateTime: userExclusionSpaUntil.value,
      appName: appName(),
    });
  }

  return t('account.user_data.exclusion_by_spa', {
    months: userExclusionSpaPeriod.value,
    dateTime: userExclusionSpaUntil.value,
    appName: appName(),
  });
});

const isSpaExclusionNoteVisible = computed<boolean>(() => {
  return !!userExclusionSpaNote.value && userExclusionSpaPeriod.value < 100;
});

async function verifyLimits() {
  const currentTime = Date.now();

  if (currentTime - lastRequestTime.value < 30000) {
    openToaster({
      message: t('account.user_data.errors.wait_30_seconds'),
      type: 'warning',
    });

    return;
  }

  showLoading.value = true;

  if (userExclusionSpaPeriod.value >= 100) {
    await fetchUserProfile({ force: true, isSpa: true });
  } else {
    await fetchUserProfile({ force: true });
  }

  showLoading.value = false;

  lastRequestTime.value = currentTime;
}
</script>

<template>
  <div
    v-if="isAuthenticated && userCanOnlyWithdraw"
    class="alert alert-warning px-5 md:px-6 py-4 mb-5"
  >
    <template v-if="isWithinMigrationPeriod">
      <span
        v-html="
          $t('account.user_data.blocked_by_migration', {
            appName: appName(),
            newDomain: userMigrationNewDomain,
            migrateDate: getFormattedBrazilianDate(userMigrationDate),
          })
        "
      />
    </template>

    <template v-else-if="userIsOnExclusionSpaActive">
      <div class="flex flex-col gap-1 mb-3">
        <p v-html="spaExclusionMessage" />

        <strong v-if="isSpaExclusionNoteVisible">
          {{ userExclusionSpaNote }}
        </strong>
      </div>
    </template>

    <template v-else-if="userIsOnExclusionOperatorActive">
      <div class="flex flex-col gap-1 mb-3">
        <p
          v-html="
            $t('account.user_data.exclusion_by_operator', {
              dateTime: userExclusionOperatorUntil,
              appName: appName(),
            })
          "
        />

        <strong>
          {{ userExclusionOperatorNote || '- - -' }}
        </strong>
      </div>
    </template>

    <template v-else-if="userIsOnExclusionActive">
      <p
        v-if="userIsOnExclusionPermanentActive"
        v-html="$t('account.self_exclusion.exclusion_permanent_active_alert')"
      />
      <p
        v-else
        v-html="
          $t('account.self_exclusion.exclusion_active_alert', {
            months: userLimits?.user_exclusion,
            date: userExclusionUntil,
          })
        "
      />
    </template>

    <template v-else-if="userIsOnTimeoutActive">
      <p
        v-html="
          $t('account.timeout_limits.timeout_active_alert', {
            days: userLimits?.user_pause,
            date: userTimeoutUntil,
          })
        "
      >
      </p>
    </template>

    <template v-else-if="isUserExceedTimeoutLimit">
      <p
        v-html="
          $t('account.account_protection.timeout_limit_alert', {
            period: findPeriodLabel(String(userLimits?.user_limit_time_period)),
          })
        "
      ></p>
    </template>

    <template v-else>
      <span>{{ $t('account.user_data.limit_on_account') }}</span>
      <NuxtLink
        class="underline"
        to="/user/account-protection"
      >
        {{ $t('account_menu.account_protection') }}
      </NuxtLink>
    </template>

    <button
      v-if="!isWithinMigrationPeriod"
      class="bg-warning/20 text-xs px-2 ml-1 md:ml-2"
      @click="verifyLimits"
    >
      {{ $t('general.verify_again') }}
    </button>

    <button
      v-else
      class="bg-warning/20 text-xs px-2 ml-1 md:ml-2"
      @click="redirectToPendingSettings()"
    >
      {{ $t('account.user_data.verify_account') }}
    </button>
  </div>

  <BaseLoading
    v-if="userCanOnlyWithdraw"
    class="!bg-auth-bg-primary !fixed"
    :icon-loader="true"
    :show="showLoading"
  />
</template>
